<template>
  <VDialog v-model="visible" max-width="450" persistent>
    <VCard class="update-notification-card">
      <!-- Icon and Title Section -->
      <div class="update-header">
        <div class="update-icon-wrapper">
          <VIcon icon="mdi-cloud-download" size="48" class="update-icon" />
        </div>
        <VCardTitle class="text-center pb-2">
          <span class="update-title">{{ title }}</span>
        </VCardTitle>
        <VCardSubtitle class="text-center">
          {{ message }}
        </VCardSubtitle>
      </div>

      <VDivider class="my-4" />

      <!-- Features Section (Optional) -->
      <VCardText v-if="features && features.length > 0" class="pb-2">
        <div class="update-features">
          <div class="text-subtitle-2 mb-2">What's new:</div>
          <ul class="update-features-list">
            <li v-for="(feature, index) in features" :key="index">
              {{ feature }}
            </li>
          </ul>
        </div>
      </VCardText>

      <!-- Actions -->
      <VCardActions class="pa-4 pt-2">
        <VSpacer />
        <VBtn variant="text" @click="handleLater" :disabled="loading">
          {{ laterText }}
        </VBtn>
        <VBtn color="primary" variant="flat" @click="handleUpdate" :loading="loading">
          <VIcon icon="mdi-refresh" start />
          {{ updateText }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  modelValue?: boolean
  title?: string
  message?: string
  updateText?: string
  laterText?: string
  features?: string[]
  autoReload?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: 'Update Available',
  message: 'A new version of the app is ready to install.',
  updateText: 'Update Now',
  laterText: 'Later',
  features: () => [],
  autoReload: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  update: []
  later: []
}>()

const loading = ref(false)

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const handleUpdate = async () => {
  loading.value = true
  emit('update')

  // If autoReload is enabled, reload after a short delay
  if (props.autoReload) {
    setTimeout(() => {
      window.location.reload()
    }, 500)
  }
}

const handleLater = () => {
  visible.value = false
  emit('later')
}
</script>

<style scoped>
.update-notification-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.update-header {
  padding: 24px 24px 0;
  text-align: center;
}

.update-icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.update-icon {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: pulse 2s ease-in-out infinite;
}

.update-title {
  font-size: 1.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.update-features {
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 8px;
  padding: 12px 16px;
}

.update-features-list {
  margin: 0;
  padding-left: 20px;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.8;
}

.update-features-list li {
  margin: 4px 0;
  font-size: 0.875rem;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

/* Dark mode adjustments */
.v-theme--dark .update-features {
  background: rgba(255, 255, 255, 0.05);
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .update-notification-card {
    margin: 16px;
  }
}
</style>
